import CampaignCard from '@components/campaign-card/campaign-card.component'
import SocialNetworkFilterComponent from '@components/social-network-filter/social-network-filter.component'
import { withTransaction } from '@elastic/apm-rum-react'
import {
  Campaign,
  CampaignQuantityBySocialNetwork,
  HubCampaignsService,
  InfinityScroll,
  LogService,
  User,
  UserInCampaign,
  onUserChange,
} from '@hub/shared'
import { SqLoader } from '@squidit/react-css'
import CampaignType from '@src/enums/campaign-type.enum'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router'
import { SubscriptionLike } from 'rxjs'
import './campaign-list-type.page.scoped.scss'

const ListCampaignPageComponent = (props: any) => {
  const { campaignType } = useParams<{ campaignType: CampaignType }>()
  const onUserChangeSubscription = useRef<SubscriptionLike | undefined>(undefined)
  const [t] = useTranslation('homeCampaign')
  const logService = useMemo(() => new LogService(), [])
  const hubCampaignsService = useMemo(() => new HubCampaignsService(), [])
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [nextPage, setNextPage] = useState<boolean>(true)
  const [page, setPage] = useState<number>(1)
  const [limit, setLimit] = useState<number>(18)
  const navigate = useNavigate()
  const [loadingList, setLoadingList] = useState<boolean>(true)
  const [loadingListMore, setLoadingListMore] = useState<boolean>(false)
  const [campaignTypeList, setCampaignTypeList] = useState<CampaignType>()
  const [socialNetwork, setSocialNetwork] = useState<'instagram' | 'twitter' | 'youtube' | 'tiktok' | ''>('')
  const [campaignQuantity, setCampaignQuantity] = useState<CampaignQuantityBySocialNetwork | null>(null)
  const [campaignsList, setCampaigns] = useState<Array<{ campaign: Campaign; additionalInformation: UserInCampaign }>>([])
  logService.init('Campaign List Type Page')

  const fetchCampaigns = useCallback(
    async (socialNetwork: 'instagram' | 'twitter' | 'youtube' | 'tiktok' | '', pageFilter: number = 1, loadMore: boolean = false) => {
      hubCampaignsService
        .getListCampaignsByType(CampaignType[campaignType], socialNetwork, pageFilter || page, limit)
        .then((response: any) => {
          if (loadMore) {
            setCampaigns([...campaignsList, ...response?.data])
          } else {
            setCampaigns(response?.data)
          }
          const newPage = pageFilter + 1
          setPage(newPage)
          setNextPage(response?.total > campaignsList.length)
          setLoadingList(false)
        })
        .catch((err) => {
          if (err) {
            logService.error({ message: err?.message, error: err })
          } else {
            logService.error({ message: 'Unknown error occurred', error: {} })
          }
          setLoadingList(false)
        })
    },
    [hubCampaignsService, campaignsList, campaignType, logService, limit, page]
  )

  const handleChangeFilter = useCallback(
    (socialNetworkFilter: 'instagram' | 'twitter' | 'youtube' | 'tiktok' | '') => {
      setLoadingList(true)
      setSocialNetwork(socialNetworkFilter)
      fetchCampaigns(socialNetworkFilter)
    },
    [fetchCampaigns]
  )

  const handleChange = useCallback(
    (socialNetwork: 'instagram' | 'twitter' | 'youtube' | 'tiktok' | ''): void => {
      fetchCampaigns(socialNetwork)
    },
    [fetchCampaigns]
  )

  const onLoadMore = useCallback(async () => {
    if (!loadingListMore && nextPage) {
      try {
        setLoadingListMore(true)
        fetchCampaigns(socialNetwork, page, true)
      } catch (error) {
        logService.error({ message: 'Failed to get more campaigns', error })
      } finally {
        setLoadingListMore(false)
      }
    }
  }, [loadingListMore, page, nextPage, fetchCampaigns, socialNetwork, logService])

  useEffect(() => {
    onUserChangeSubscription.current = onUserChange?.subscribe((userChanged: User) => {
      setUser(userChanged)
      setLoading(false)
    })

    return () => onUserChangeSubscription?.current?.unsubscribe()
  }, [])

  useEffect(() => {
    if (!CampaignType?.[campaignType as CampaignType]) {
      if (campaignType?.match(/^[0-9a-fA-F]{24}$/)) {
        navigate(`/campaigns/${campaignType}/summary`)
      } else {
        history.pushState({}, '', '/error/not-found')
      }
    }
  }, [campaignType, navigate])

  useEffect(() => {
    if (user && campaignTypeList !== campaignType) {
      setLoadingList(true)
      setCampaignTypeList(campaignType)
      setSocialNetwork(socialNetwork)
      handleChange(socialNetwork)
    }
  }, [user, socialNetwork, campaignTypeList, handleChange, campaignType])

  if (loading) {
    return (
      <div className="text-center my-5" style={{ width: '100%' }}>
        <SqLoader size="big" />
      </div>
    )
  }

  return (
    <main className="campaign-list-type-page box mb-0">
      <header className="mb-3">
        <div className="header">
          <div className="mb-3 display-flex align-items-center">
            <div className="mr-2 back-circle" role="button" tabIndex={0} onClick={() => navigate('/campaigns')}>
              <i className="fa-solid fa-arrow-left" style={{ fontSize: '16px' }}></i>
            </div>
            <h1>{t(`${campaignTypeList}.title`)}</h1>
          </div>
          <SocialNetworkFilterComponent onChange={(value) => handleChangeFilter(value)} quantity={campaignQuantity} loading={loadingList} />
        </div>
      </header>
      <div className="content">
        {loadingList ? (
          <div className="text-center my-5" style={{ width: '100%' }}>
            <SqLoader size="big" />
          </div>
        ) : campaignsList?.length > 0 ? (
          <InfinityScroll
            className="campaign-list-type-infinity-scroll"
            loading={loadingListMore}
            onLoadMore={onLoadMore}
            hasMore={!!nextPage}
            endMessage={' '}
          >
            <ul>
              {campaignsList?.map((c, index) => (
                <CampaignCard campaign={c?.campaign} users={c?.additionalInformation} key={`${index}-${c?.campaign?._id}`} />
              ))}
            </ul>
          </InfinityScroll>
        ) : (
          <div className="without-campaign-warning">
            <h5 className="m-0" title={t(`${CampaignType?.[campaignTypeList as CampaignType]}.notFound`)}>
              {t(`${campaignTypeList}.notFound`)}
            </h5>
          </div>
        )}
      </div>
    </main>
  )
}

export default withTransaction('ListCampaignPageComponent', 'component')(ListCampaignPageComponent)
